import { Link } from 'react-router-dom';
import { useQuery, useQueries } from '@tanstack/react-query';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Plus } from 'lucide-react';
import { useOrgs } from '@/contexts/OrgsContext';
import { useRoles } from '@/contexts/RolesContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { postListProfiles, getProfilesById } from '@/api/control/control-plane/control-plane';
import { EntitlementsCard } from '@/components/EntitlementsCard';
import { OrgAlerts } from '@/components/OrgAlerts';
import { SystemNoticesCard } from '@/components/notices/SystemNoticesCard';
import { AgentUpdateAlert } from '@/components/agents/AgentUpdateAlert';
import { AgentOfflineAlert } from '@/components/agents/AgentOfflineAlert';
import { ProfileRunSummaryCard } from '@/components/runs/ProfileRunSummaryCard';
import { readBurstExpiresUtc } from '@/api/burst-control';
import { QueryErrorState } from '@/components/common/QueryErrorState';
import type {
  ProfileDto,
} from '@/api/control/missionImpactAPI.schemas';
import { PageHeader } from '@/components/common/PageHeader';

/**
 * Dashboard home page
 */
export default function DashboardPage() {
  const { selectedOrganizationId } = useOrgs();
  const { isAdminOrOperator } = useRoles();
  const { t } = useLanguage();

  // Fetch profiles list to count total profiles
  const { data: profilesData, isLoading: isLoadingProfiles, error: profilesError, refetch: refetchProfiles } = useQuery({
    queryKey: ['profiles', 'list', selectedOrganizationId],
    queryFn: async () => {
      const response = await postListProfiles(selectedOrganizationId ?? '', { pageSize: 100 });
      return response;
    },
    enabled: !!selectedOrganizationId,
    retry: 1,
  });

  const profiles: ProfileDto[] = profilesData?.data.items ?? [];
  
  // Fetch detailed profile data for each profile to check completeness
  const profileQueries = useQueries({
    queries: profiles.map((profile) => ({
      queryKey: ['profiles', 'detail', selectedOrganizationId, profile.id],
      queryFn: async () => {
        const response = await getProfilesById(selectedOrganizationId ?? '', profile.id);
        return response.data;
      },
      enabled: !!selectedOrganizationId && !!profile.id,
      retry: 1,
      staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    })),
  });

  // Check if still loading any profile details
  const isLoadingDetails = profileQueries.some((query) => query.isLoading);

  // Filter to get only complete profiles (have both source and target)
  const completeProfiles = profiles
    .map((profile, index) => {
      const detailedProfile = profileQueries[index]?.data;
      return {
        profile,
        isComplete: !!(detailedProfile?.source && detailedProfile.target),
      };
    })
    .filter((item) => item.isComplete)
    .map((item) => item.profile);

  return (
    <div className="space-y-6">
      <PageHeader title={t.dashboard.title} description={t.dashboard.description} />

      {/* Show error if profiles list failed to load */}
      {profilesError && (
        <QueryErrorState 
          error={profilesError} 
          message={t.errors.profiles.failedToLoadProfiles}
          onRetry={() => { void refetchProfiles(); }}
        />
      )}

      {/* Entitlements Summary Card */}
      <div className="flex flex-wrap gap-6 items-stretch">
        <EntitlementsCard />
        <OrgAlerts />
        <SystemNoticesCard />
        <AgentOfflineAlert />
        <AgentUpdateAlert />
      </div>

      {/* Profile Run Summary Cards */}
      <div>
        <h2 className="text-xl font-semibold mb-4">{t.dashboard.profileActivity}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {isLoadingProfiles || isLoadingDetails ? (
            <>
              {[1, 2, 3].map((i) => (
                <Card key={i}>
                  <CardHeader>
                    <Skeleton className="h-5 w-32 mb-2" />
                    <Skeleton className="h-4 w-24" />
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-8 w-16" />
                    </div>
                    <div className="space-y-3">
                      <Skeleton className="h-5 w-full" />
                      <Skeleton className="h-5 w-full" />
                      <Skeleton className="h-5 w-full" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </>
          ) : (
            <>
              {completeProfiles.map((profile) => (
                <ProfileRunSummaryCard
                  key={profile.id}
                  orgId={selectedOrganizationId ?? ''}
                  profileId={profile.id}
                  profileName={profile.name}
                  isEnabled={profile.isEnabled}
                  burstExpiresUtc={readBurstExpiresUtc(profile)}
                />
              ))}

              {/* Create New Profile Card */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    {isAdminOrOperator ? (
                      <Link to={`/dashboard/orgs/${String(selectedOrganizationId)}/profiles/wizard/profile`}>
                        <Card className="hover:background-neutral transition-colors border-dashed h-full flex items-center justify-center min-h-[200px]">
                          <CardContent className="flex flex-col items-center justify-center py-8">
                            <Plus className="h-8 w-8 text-muted-foreground mb-2" />
                            <p className="text-sm text-muted-foreground">{t.dashboard.createNewProfile}</p>
                          </CardContent>
                        </Card>
                      </Link>
                    ) : (
                      <Card className="border-dashed h-full flex items-center justify-center min-h-[200px] opacity-50 cursor-not-allowed">
                        <CardContent className="flex flex-col items-center justify-center py-8">
                          <Plus className="h-8 w-8 text-muted-foreground mb-2" />
                          <p className="text-sm text-muted-foreground">{t.dashboard.createNewProfile}</p>
                        </CardContent>
                      </Card>
                    )}
                  </TooltipTrigger>
                  {!isAdminOrOperator && (
                    <TooltipContent>
                      <p>{t.profiles.noPermission}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
